import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

const BRACKETS_2026 = {
  single: [[0,12500,10],[12500,50700,12],[50700,105700,22],[105700,201775,24],[201775,256225,32],[256225,640600,35],[640600,Infinity,37]],
  mfj: [[0,25000,10],[25000,101400,12],[101400,211400,22],[211400,403550,24],[403550,512450,32],[512450,768800,35],[768800,Infinity,37]],
  hoh: [[0,17850,10],[17850,68150,12],[68150,105700,22],[105700,201775,24],[201775,256225,32],[256225,640600,35],[640600,Infinity,37]],
  widow: [[0,25000,10],[25000,101400,12],[101400,211400,22],[211400,403550,24],[403550,512450,32],[512450,768800,35],[768800,Infinity,37]],
};
const STD = { single: 16000, mfj: 32000, hoh: 24000, widow: 32000 };
const fedTax = (income, filing) => {
  const b = BRACKETS_2026[filing] || BRACKETS_2026.single;
  let t = 0; for (const [lo, hi, r] of b) { if (income > lo) t += (Math.min(income, hi) - lo) * r/100; if (income <= hi) break; } return t;
};

// Take-Home-Paycheck Calculator
export default function TakeHomePayCalc() {
  const [income, setIncome] = useState(80000);
  const [freq, setFreq] = useState('biweekly');
  const [filing, setFiling] = useState('single');
  const [under17, setUnder17] = useState(0);
  const [otherDep, setOtherDep] = useState(0);
  const [otherIncome, setOtherIncome] = useState(0);
  const [preTax, setPreTax] = useState(6000);
  const [postTax, setPostTax] = useState(0);
  const [itemized, setItemized] = useState(0);
  const [job2, setJob2] = useState('no');
  const [stateRate, setStateRate] = useState(0);
  const [cityRate, setCityRate] = useState(0);
  const [age65, setAge65] = useState('no');
  const [tips, setTips] = useState(0);
  const [ot, setOt] = useState(0);
  const [carLoan, setCarLoan] = useState(0);
  const [cashGifts, setCashGifts] = useState(0);
  const [selfEmp, setSelfEmp] = useState('no');

  const c = useMemo(() => {
    const grossAnnual = income + otherIncome;
    const dedTotal = Math.max(STD[filing] || STD.single, itemized) + postTax;
    const preTaxTotal = preTax;
    const taxableIncome = Math.max(0, grossAnnual - preTaxTotal - dedTotal);
    const federal = fedTax(taxableIncome, filing);
    const ctc = under17 * 2000;
    const odc = otherDep * 500;
    const fedAfterCredits = Math.max(0, federal - ctc - odc);
    const fica = income * 0.0765; // SS + Medicare
    const seTax = selfEmp === 'yes' ? income * 0.153 : 0;
    const state = taxableIncome * stateRate/100;
    const city = taxableIncome * cityRate/100;
    const totalTax = fedAfterCredits + fica + seTax + state + city;
    const netAnnual = grossAnnual - preTaxTotal - totalTax - postTax;
    const perPeriod = {
      weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12, quarterly: 4, annually: 1, daily: 260,
    };
    const paychecks = perPeriod[freq];
    const takeHome = netAnnual / paychecks;
    return { grossAnnual, taxableIncome, federal, ctc, odc, fedAfterCredits, fica, seTax, state, city, totalTax, netAnnual, takeHome, paychecks };
  }, [income, freq, filing, under17, otherDep, otherIncome, preTax, postTax, itemized, stateRate, cityRate, tips, ot, carLoan, cashGifts, selfEmp]);

  const $ = (v, on) => <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={v} onChange={e=>on(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">/year</span></div>;
  const YesNo = ({ cur, setter }) => <div className="flex gap-4 mt-1"><label><input type="radio" checked={cur==='yes'} onChange={()=>setter('yes')}/> Yes</label><label><input type="radio" checked={cur==='no'} onChange={()=>setter('no')}/> No</label></div>;

  return (
    <CalcShell look="cnet" title="Take-Home-Paycheck Calculator" subtitle="Estimate the actual paycheck brought home after federal, state, city, FICA, and pre-tax deductions."
      about={<><p>Estimates your net paycheck after federal + state + city taxes, FICA, and pre-tax/post-tax deductions. Uses 2026 tax brackets and the new W-4 reflecting recent tax law changes. Intended for U.S. residents.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your job income (salary)</label>{$(income, setIncome)}</div>
          <div className="field"><label>Pay frequency</label>
            <select value={freq} onChange={e=>setFreq(e.target.value)}>
              <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="semimonthly">Semi-monthly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annually">Annually</option><option value="daily">Daily</option>
            </select>
          </div>
          <div className="field"><label>File status</label>
            <select value={filing} onChange={e=>setFiling(e.target.value)}><option value="single">Single</option><option value="mfj">Married Filing Jointly</option><option value="hoh">Head of Household</option><option value="widow">Qualifying Widow(er)</option></select>
          </div>
          <div className="field"><label>Number of children under age 17</label><input type="number" value={under17} onChange={e=>setUnder17(+e.target.value||0)}/></div>
          <div className="field"><label>Number of other dependents</label><input type="number" value={otherDep} onChange={e=>setOtherDep(+e.target.value||0)}/></div>
          <div className="field"><label>Other income (not from jobs)</label>{$(otherIncome, setOtherIncome)}<p className="text-xs text-slate-500 mt-1">interest, dividends, retirement income, etc.</p></div>
          <div className="field"><label>Pretax deductions withheld</label>{$(preTax, setPreTax)}<p className="text-xs text-slate-500 mt-1">401k, health insurance, HSA, etc.</p></div>
          <div className="field"><label>Deductions not withheld</label>{$(postTax, setPostTax)}<p className="text-xs text-slate-500 mt-1">IRA, student loan interest, etc.</p></div>
          <div className="field"><label>Itemized deductions</label>{$(itemized, setItemized)}<p className="text-xs text-slate-500 mt-1">mortgage interest, charitable, state/local/property taxes, etc.</p></div>
          <div className="field"><label>Has 2nd, 3rd job income?</label><YesNo cur={job2} setter={setJob2}/></div>
          <div className="field"><label>State income tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={stateRate} onChange={e=>setStateRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>City income tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={cityRate} onChange={e=>setCityRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Are you age 65 or older?</label><YesNo cur={age65} setter={setAge65}/></div>
          <div className="field"><label>Qualified tips income</label>{$(tips, setTips)}</div>
          <div className="field"><label>Qualified overtime compensation</label>{$(ot, setOt)}</div>
          <div className="field"><label>Qualified passenger vehicle loan interest</label>{$(carLoan, setCarLoan)}</div>
          <div className="field"><label>Cash gifts to charities</label>{$(cashGifts, setCashGifts)}</div>
          <div className="field"><label>Are you self-employed or an independent contractor?</label><YesNo cur={selfEmp} setter={setSelfEmp}/></div>
        </div>
        <div className="lg:sticky lg:top-4 self-start">
          <div className="result-panel mb-4"><div className="result-title">Take-Home Pay per {freq}</div><div className="result-value" data-testid="takehome-per">{fmt(c.takeHome)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Gross annual pay</td><td className="text-right">{fmt(c.grossAnnual)}</td></tr>
            <tr><td>Pre-tax deductions</td><td className="text-right">-{fmt(preTax)}</td></tr>
            <tr><td>Taxable income</td><td className="text-right font-semibold">{fmt(c.taxableIncome)}</td></tr>
            <tr><td>Federal income tax</td><td className="text-right text-red-600">-{fmt(c.fedAfterCredits)}</td></tr>
            <tr><td>Social Security + Medicare (7.65%)</td><td className="text-right text-red-600">-{fmt(c.fica)}</td></tr>
            {c.seTax > 0 && <tr><td>Self-employment tax</td><td className="text-right text-red-600">-{fmt(c.seTax)}</td></tr>}
            <tr><td>State income tax</td><td className="text-right text-red-600">-{fmt(c.state)}</td></tr>
            <tr><td>City income tax</td><td className="text-right text-red-600">-{fmt(c.city)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Net annual take-home</td><td className="text-right text-green-700">{fmt(c.netAnnual)}</td></tr>
            <tr><td>Paychecks per year</td><td className="text-right">{c.paychecks}</td></tr>
          </tbody></table>
        </div>
      </div>
    </CalcShell>
  );
}
